/**
 * AddressLookupV2 — non-breaking replacement for the legacy AddressLookup.tsx.
 *
 * Diagnosis (full audit at /Documents/KitkeeperCore/POSTCODER_AUDIT.md):
 *   The legacy component fires an unguarded fetch on every keystroke with no
 *   debounce, no AbortController, and no in-flight tracking. Older requests
 *   resolve after newer ones and overwrite the suggestions state — the UI ends
 *   up showing matches for an earlier (shorter) query, or nothing at all if
 *   the API returns an opaque error blob (then `Array.filter` throws inside an
 *   IIFE and the error is silently swallowed).
 *
 * v2 fixes:
 *   - 300 ms debounce
 *   - AbortController per request, latest-wins
 *   - explicit loading / empty / error UX with retry affordance
 *   - WAI-ARIA combobox 1.2 with keyboard nav (ArrowUp/ArrowDown/Enter/Escape)
 *   - mousedown-preventDefault on suggestions so input blur does not race click
 *   - 2-character minimum query gate ("Keep typing…")
 *   - try/catch around all fetches (Postcoder error blob no longer fatal)
 *   - smarter empty + error microcopy (Phase 2)
 *   - bolded substring match in suggestions (Phase 2)
 *   - two-line render with secondary postcode + locality (Phase 2)
 *   - outcode grouping when ≥ 8 suggestions (Phase 2)
 *   - Postcoder drill-down via `pathfilter` (Phase 3 hotfix). Coarse queries
 *     return STR/PCD/LOC rows with count > 1; clicking one re-runs `find`
 *     with `pathfilter=<row.id>` to narrow to the next level. ADD-typed
 *     rows or rows with count === 1 are terminal — clicking one calls
 *     `retrieve` and fills the form. Pre-Phase-3 the picker filtered to
 *     `type === 'ADD'` only and dropped 100% of results for coarse queries.
 *
 * Phase 4 Track 4.8 (2026-09-08) — the picker's own chrome:
 *   - Root is `.address-lookup-v2` only. It used to also carry
 *     `coll-del-div addressLookup`, borrowing a 400 x 350px fixed-size card
 *     from `_landing.scss`'s unscoped `form { … }` block that then had to be
 *     fought back with doubled-class specificity hacks. The card is the
 *     page's job (<BookingLeg>); the picker is a field group.
 *   - Search field: real `<label>` above the input, pin icon inside it, and
 *     a hint saying what to do with the list.
 *   - Address lines: label above input on the shared token chrome, laid out
 *     on a grid, with `autoComplete` so the browser can offer what it knows.
 *   - `fieldErrors` renders per-field messages wired through `aria-invalid` +
 *     `aria-describedby`.
 *
 * The public prop signature is otherwise identical to AddressLookupProps in
 * the legacy module, so a swap is one import change. Helper exports
 * (validateAddress / simplifyAddress / testPostCode / getCityFromPostcode /
 *  isPostcodeInAllowedCities) remain in the legacy module — consumers either
 * stay on `import AddressLookup from '../components/AddressLookup/AddressLookupV2'` or
 * swap to `import AddressLookupV2 from '../components/AddressLookup/AddressLookupV2'`.
 */
import React, {
  ChangeEvent,
  DOMAttributes,
  FocusEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { MapPinIcon } from '@heroicons/react/24/solid';
import { ChevronRightIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';

import {
  isPostcodeInAllowedCities,
  getCityFromPostcode,
  simplifyAddress,
  validateAddress,
} from './address';
import type { Address } from './address';
import Button from '../Button/Button';
import {
  findAddresses,
  retrieveAddress,
  PostcoderError,
  PostCoderFind,
} from './postcoder';

export interface AddressLookupV2Props
  extends DOMAttributes<AddressLookupV2Props> {
  address: Address;
  defaultValue?: string;
  setAddress: (address: Address, addressString: string) => void;
  setValid?: (valid: boolean) => void;
  name?: string | undefined;
  callback?: (event: any) => void;
  eecBillingInfo?: any;
  billingInfo?: any;
  billingEventSentToGTM?: boolean;
  setBillingEventSentToGTM?: (status: boolean) => void;
  onlyUnitedKingdom?: boolean;
  idPrefix?: string;
  allowedCities?: string[];
  label?: string;
  /**
   * Per-field messages, keyed by `Address` field. Rendered under the field
   * they belong to and wired to it through `aria-describedby` + `aria-invalid`.
   * The page owns these — validity is a question about the booking, not about
   * the picker.
   */
  fieldErrors?: Partial<Record<keyof Address, string | null>>;
}

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 300;
const GROUPING_THRESHOLD = 8;

let idCounter = 0;
function generateId(): string {
  idCounter += 1;
  return `al-v2-${idCounter}`;
}

type SuggestionsState =
  | { kind: 'idle' }
  | { kind: 'tooShort' }
  | { kind: 'loading' }
  | { kind: 'ready'; items: PostCoderFind[] }
  | { kind: 'empty' }
  | { kind: 'error'; message: string };

// ── Summaryline parsing ────────────────────────────────────────────────────
//
// Postcoder's autocomplete `find` endpoint with `singlesummary=true` returns
// one `summaryline` per suggestion. UK summarylines are typically formatted as
//   "{building/number} {street}, {locality}, {posttown} {postcode}"
// or
//   "{building/number} {street}, {posttown} {postcode}"
// We split on the LAST comma so line 1 carries the building + street (and
// locality if present) and line 2 carries the post-town + postcode. When no
// comma is present we render a single line.

interface SuggestionLines {
  primary: string;
  secondary: string | null;
}

function splitSummaryline(summaryline: string): SuggestionLines {
  const lastComma = summaryline.lastIndexOf(',');
  if (lastComma === -1) {
    return { primary: summaryline.trim(), secondary: null };
  }
  const primary = summaryline.slice(0, lastComma).trim();
  const secondary = summaryline.slice(lastComma + 1).trim();
  if (!primary || !secondary) {
    return { primary: summaryline.trim(), secondary: null };
  }
  return { primary, secondary };
}

// ── Outcode extraction ─────────────────────────────────────────────────────
//
// UK postcode outcode is the first half (e.g. SW1A 1AA → "SW1A", M1 1AA →
// "M1", EC1V 9HQ → "EC1V"). We look for the standard pattern at the start of
// the secondary segment which typically reads "{posttown} {postcode}". The
// regex matches one or two letters, one or two digits, optional final letter
// — which is the canonical UK outcode shape.
const OUTCODE_PATTERN = /\b([A-Z]{1,2}\d{1,2}[A-Z]?)\b/;

function extractOutcode(summaryline: string): string | null {
  const match = summaryline.toUpperCase().match(OUTCODE_PATTERN);
  return match ? match[1] : null;
}

// ── Match-highlighting ─────────────────────────────────────────────────────
//
// Splits `text` on the (case-insensitive) first occurrence of `query`, returning
// the three segments around the match. Renders the matched substring in
// `<strong>` so the user sees what they typed echoed in the suggestion.
function highlightMatch(text: string, query: string): React.ReactNode {
  const trimmed = query.trim();
  if (!trimmed) return text;
  const idx = text.toLowerCase().indexOf(trimmed.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <strong className="kk-address-suggestion__match">
        {text.slice(idx, idx + trimmed.length)}
      </strong>
      {text.slice(idx + trimmed.length)}
    </>
  );
}

export default function AddressLookupV2({
  address,
  setAddress,
  setValid,
  children,
  defaultValue = '',
  callback,
  eecBillingInfo,
  billingInfo,
  billingEventSentToGTM,
  setBillingEventSentToGTM,
  onlyUnitedKingdom,
  idPrefix = '',
  allowedCities,
  label = 'Address lookup',
  fieldErrors,
}: AddressLookupV2Props) {
  const [query, setQuery] = useState<string>(defaultValue);
  const [state, setState] = useState<SuggestionsState>({ kind: 'idle' });
  const [active, setActive] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const [cityValidationError, setCityValidationError] = useState<string | null>(
    null,
  );
  // Bumped on every retry click so the debounce effect re-fires for the
  // current query without changing it.
  const [retryToken, setRetryToken] = useState(0);
  // Drill-down state. When non-null, the listbox is showing the addresses
  // under a previously-clicked STR/PCD/LOC suggestion. `pathfilter` is the
  // suggestion id to pass back into `findAddresses`; `summaryline` is the
  // human-readable label rendered in the "Back to all matches" affordance.
  const [drilldown, setDrilldown] = useState<{
    pathfilter: string;
    summaryline: string;
  } | null>(null);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxRef = useRef<HTMLUListElement>(null);

  // Stable IDs for ARIA wiring. React 17 has no useId hook, so we use a
  // module-level counter via a ref initialiser to keep ids stable per mount.
  const idSuffixRef = useRef<string | null>(null);
  if (idSuffixRef.current === null) {
    idSuffixRef.current = generateId();
  }
  const reactId = idSuffixRef.current;
  const inputId = idPrefix ? `${idPrefix}-lookup` : `${reactId}-lookup`;
  const listboxId = idPrefix
    ? `${idPrefix}-lookup-listbox`
    : `${reactId}-listbox`;
  const statusId = idPrefix ? `${idPrefix}-lookup-status` : `${reactId}-status`;
  const optionId = useCallback(
    (i: number) => `${listboxId}-option-${i}`,
    [listboxId],
  );

  const items = useMemo(
    () => (state.kind === 'ready' ? state.items : []),
    [state],
  );

  // Group suggestions by outcode when over the grouping threshold. Returns a
  // flat list of "rows" — either a group header or a suggestion. Headers
  // carry the outcode label and the index of the first suggestion in the
  // group, so screen readers and keyboard nav can stay on real options only.
  type Row =
    | { kind: 'header'; outcode: string }
    | { kind: 'option'; suggestion: PostCoderFind; index: number };
  const rows: Row[] = useMemo(() => {
    if (items.length < GROUPING_THRESHOLD) {
      return items.map((suggestion, index) => ({
        kind: 'option' as const,
        suggestion,
        index,
      }));
    }
    const result: Row[] = [];
    let currentOutcode: string | null = null;
    items.forEach((suggestion, index) => {
      const outcode = extractOutcode(suggestion.summaryline) ?? 'OTHER';
      if (outcode !== currentOutcode) {
        result.push({ kind: 'header', outcode });
        currentOutcode = outcode;
      }
      result.push({ kind: 'option', suggestion, index });
    });
    return result;
  }, [items]);

  // ── Click-outside (mousedown). Closes only when the click target is outside
  //    the entire wrapper, including the listbox.
  useEffect(() => {
    function handleMousedown(event: Event) {
      const node = event.target as Node;
      if (wrapperRef.current && !wrapperRef.current.contains(node)) {
        setActive(false);
      }
    }
    document.addEventListener('mousedown', handleMousedown);
    return () => document.removeEventListener('mousedown', handleMousedown);
  }, []);

  // ── allowedCities validation + GTM callback wiring (parity with legacy).
  useEffect(() => {
    if (allowedCities && address.postCode) {
      const isAllowed = isPostcodeInAllowedCities(
        address.postCode,
        allowedCities,
      );
      const detectedCity = getCityFromPostcode(address.postCode);
      if (!isAllowed && detectedCity) {
        setCityValidationError(
          `Sorry, we currently only service ${allowedCities.join(', ')} for removals. The postcode you entered appears to be in ${detectedCity}.`,
        );
      } else if (!isAllowed && !detectedCity) {
        setCityValidationError(
          `Sorry, we currently only service ${allowedCities.join(', ')} for removals.`,
        );
      } else {
        setCityValidationError(null);
      }
    } else {
      setCityValidationError(null);
    }

    const isAddressValid = validateAddress(address) && !cityValidationError;
    if (setValid) {
      setValid(!!isAddressValid);
    }

    if (
      isAddressValid &&
      callback &&
      !billingEventSentToGTM &&
      setBillingEventSentToGTM
    ) {
      setBillingEventSentToGTM(true);
      callback(eecBillingInfo);
      callback(billingInfo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, setValid, allowedCities, cityValidationError]);

  // ── Debounced + abortable fetch on query change.
  //    Latest-wins: every keystroke aborts any in-flight request before
  //    starting the timer for the next one. `retryToken` re-fires the same
  //    effect when the user clicks the Retry button. `drilldown` triggers a
  //    re-fetch with a `pathfilter` parameter to narrow the result set.
  useEffect(() => {
    const trimmed = query.trim();

    if (trimmed.length < MIN_QUERY_LENGTH) {
      setState({ kind: trimmed.length === 0 ? 'idle' : 'tooShort' });
      return;
    }

    const controller = new AbortController();
    setState({ kind: 'loading' });

    const timer = window.setTimeout(async () => {
      try {
        const results = await findAddresses(
          trimmed,
          controller.signal,
          drilldown?.pathfilter,
        );
        if (controller.signal.aborted) {
          return;
        }
        if (results.length === 0) {
          setState({ kind: 'empty' });
        } else {
          setState({ kind: 'ready', items: results });
        }
      } catch (err) {
        if ((err as Error).name === 'AbortError') {
          return;
        }
        const message =
          err instanceof PostcoderError
            ? "Couldn't load suggestions right now."
            : "Couldn't load suggestions right now.";
        setState({ kind: 'error', message });
      }
    }, DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [query, retryToken, drilldown]);

  // Reset drill-down state whenever the user types a new query. The effect
  // above will re-fire with the cleared pathfilter on the next render — we
  // don't need to abort here, the controller in the previous effect handles
  // cancellation.
  const previousQueryRef = useRef<string>(query);
  useEffect(() => {
    if (previousQueryRef.current !== query && drilldown !== null) {
      setDrilldown(null);
    }
    previousQueryRef.current = query;
  }, [query, drilldown]);

  // Reset highlight when results change.
  useEffect(() => {
    setHighlightedIndex(items.length > 0 ? 0 : -1);
  }, [items.length, state.kind]);

  // ── Selection: branches on suggestion shape.
  //    - count === 1 OR type === 'ADD' → terminal: retrieve + fill form + close.
  //    - count > 1 (STR/PCD/LOC/...)   → drill-down: re-run find with
  //      `pathfilter=<id>` to narrow. Input value stays unchanged so the user
  //      can keep typing or click a deeper row.
  const selectSuggestion = useCallback(
    async (suggestion: PostCoderFind) => {
      const isTerminal =
        suggestion.type === 'ADD' || suggestion.count === 1;

      if (!isTerminal) {
        setDrilldown({
          pathfilter: suggestion.id,
          summaryline: suggestion.summaryline,
        });
        setHighlightedIndex(0);
        // The query/retry effect re-fires on `drilldown` change and will set
        // `loading` → `ready`. Keep `active` true so the listbox stays open.
        return;
      }

      const controller = new AbortController();
      try {
        const fullAddress = await retrieveAddress(
          suggestion.id,
          query,
          controller.signal,
        );
        setAddress(fullAddress, simplifyAddress(fullAddress));
        setActive(false);
        setQuery('');
        setDrilldown(null);
        setState({ kind: 'idle' });
      } catch (err) {
        if ((err as Error).name === 'AbortError') {
          return;
        }
        setState({
          kind: 'error',
          message: "Couldn't load that address. Please try again.",
        });
      }
    },
    [query, setAddress],
  );

  // ── Back to all matches: clear drill-down + restore the input-keyed search.
  const clearDrilldown = useCallback(() => {
    setDrilldown(null);
    setHighlightedIndex(0);
  }, []);

  // ── Keyboard navigation: ArrowDown/ArrowUp/Home/End/Enter/Escape.
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!active) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        setActive(true);
        event.preventDefault();
      }
      return;
    }
    if (state.kind !== 'ready' || items.length === 0) {
      if (event.key === 'Escape') {
        setActive(false);
      }
      return;
    }
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setHighlightedIndex((i) => (i + 1) % items.length);
        break;
      case 'ArrowUp':
        event.preventDefault();
        setHighlightedIndex((i) => (i - 1 + items.length) % items.length);
        break;
      case 'Home':
        event.preventDefault();
        setHighlightedIndex(0);
        break;
      case 'End':
        event.preventDefault();
        setHighlightedIndex(items.length - 1);
        break;
      case 'Enter':
        if (highlightedIndex >= 0 && highlightedIndex < items.length) {
          event.preventDefault();
          selectSuggestion(items[highlightedIndex]);
        }
        break;
      case 'Escape':
        event.preventDefault();
        setActive(false);
        break;
      default:
        break;
    }
  };

  // ── Field-edit handlers (mirror legacy onChange behaviour).
  const onAddressFieldChange =
    (key: keyof Address) =>
    (value: ChangeEvent<HTMLInputElement> | FocusEvent<HTMLInputElement>) => {
      const updatedAddress: Address = {
        ...address,
        [key]: value.target.value,
      };
      if (onlyUnitedKingdom) {
        updatedAddress.country = 'United Kingdom';
      }
      if (setValid) {
        setValid(!!validateAddress(updatedAddress));
      }
      setAddress(updatedAddress, simplifyAddress(updatedAddress));
    };

  // Polite live-region status. Concise, screen-reader-friendly UK English.
  // Drill-down state announces both the parent context and the result count
  // so AT users understand they are inside a narrowed list.
  const statusMessage = useMemo(() => {
    switch (state.kind) {
      case 'tooShort':
        return 'Keep typing.';
      case 'loading':
        return drilldown
          ? `Searching addresses in ${drilldown.summaryline}.`
          : 'Searching addresses.';
      case 'empty':
        return drilldown ? 'No specific addresses.' : 'No matches.';
      case 'error':
        return 'Could not load suggestions.';
      case 'ready': {
        const count = state.items.length;
        if (drilldown) {
          return count === 1
            ? `Drilled into ${drilldown.summaryline}. 1 address.`
            : `Drilled into ${drilldown.summaryline}. ${count} addresses.`;
        }
        return count === 1 ? '1 match.' : `${count} matches.`;
      }
      default:
        return '';
    }
  }, [state, drilldown]);

  const trimmedQuery = query.trim();

  const hintId = `${inputId}-hint`;

  return (
    <div className="address-lookup-v2" ref={wrapperRef}>
      <div className="address-lookup-v2__search">
        <label className="address-lookup-v2__label" htmlFor={inputId}>
          {label}
        </label>

        <div className="address-lookup-v2__combobox">
          <MapPinIcon className="address-lookup-v2__icon" aria-hidden="true" />
          <input
            id={inputId}
            ref={inputRef}
            className="lookup address-lookup-v2__input"
            placeholder="Start typing a postcode or street"
            aria-describedby={hintId}
            value={query}
            autoComplete="off"
            // ARIA 1.2 combobox pattern: role + state directly on the input.
            role="combobox"
            aria-expanded={active && state.kind !== 'idle'}
            aria-controls={listboxId}
            aria-haspopup="listbox"
            aria-autocomplete="list"
            aria-activedescendant={
              active && highlightedIndex >= 0 && state.kind === 'ready'
                ? optionId(highlightedIndex)
                : undefined
            }
            onFocus={() => setActive(true)}
            onChange={(event) => {
              setQuery(event.target.value);
              setActive(true);
            }}
            onKeyDown={onKeyDown}
          />
          {active && state.kind !== 'idle' && (
            <ul
              id={listboxId}
              ref={listboxRef}
              className="address-lookup-v2__listbox"
              role="listbox"
              aria-label="Address suggestions"
            >
              {drilldown && (
                <li
                  className="address-lookup-v2__back-row"
                  role="presentation"
                >
                  <button
                    type="button"
                    className="address-lookup-v2__back-btn"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={clearDrilldown}
                  >
                    <ArrowLeftIcon
                      className="address-lookup-v2__back-icon"
                      aria-hidden="true"
                    />
                    <span className="address-lookup-v2__back-label">
                      Back to all matches
                    </span>
                  </button>
                  <span
                    className="address-lookup-v2__back-context"
                    aria-hidden="true"
                  >
                    {drilldown.summaryline}
                  </span>
                </li>
              )}
              {state.kind === 'loading' && (
                <li
                  className="address-lookup-v2__option address-lookup-v2__option--meta"
                  role="presentation"
                >
                  Searching addresses…
                </li>
              )}
              {state.kind === 'tooShort' && (
                <li
                  className="address-lookup-v2__option address-lookup-v2__option--meta"
                  role="presentation"
                >
                  Keep typing…
                </li>
              )}
              {state.kind === 'empty' && !drilldown && (
                <li
                  className="address-lookup-v2__option address-lookup-v2__option--meta address-lookup-v2__option--empty"
                  role="presentation"
                >
                  No matches for &lsquo;{trimmedQuery}&rsquo;. Try a shorter
                  postcode like &lsquo;SW1&rsquo; or check the spelling.
                </li>
              )}
              {state.kind === 'empty' && drilldown && (
                <li
                  className="address-lookup-v2__option address-lookup-v2__option--meta address-lookup-v2__option--empty"
                  role="presentation"
                >
                  No specific addresses on &lsquo;{drilldown.summaryline}
                  &rsquo;. Press &lsquo;Back to all matches&rsquo; to widen
                  the search.
                </li>
              )}
              {state.kind === 'error' && (
                <li
                  className="address-lookup-v2__option address-lookup-v2__option--error"
                  role="presentation"
                >
                  <span className="address-lookup-v2__error-text">
                    Couldn&rsquo;t load suggestions right now.
                  </span>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => setRetryToken((n) => n + 1)}
                    className="address-lookup-v2__retry"
                  >
                    Try again
                  </Button>
                </li>
              )}
              {state.kind === 'ready' &&
                rows.map((row, rowIdx) => {
                  if (row.kind === 'header') {
                    return (
                      <li
                        key={`hdr-${row.outcode}-${rowIdx}`}
                        className="address-lookup-v2__group-header"
                        aria-hidden="true"
                      >
                        {row.outcode}
                      </li>
                    );
                  }
                  const { suggestion, index } = row;
                  const lines = splitSummaryline(suggestion.summaryline);
                  const isTerminal =
                    suggestion.type === 'ADD' || suggestion.count === 1;
                  return (
                    <li
                      key={suggestion.id}
                      id={optionId(index)}
                      role="option"
                      aria-selected={index === highlightedIndex}
                      className={
                        'address-lookup-v2__option' +
                        (index === highlightedIndex
                          ? ' address-lookup-v2__option--active'
                          : '') +
                        (!isTerminal
                          ? ' address-lookup-v2__option--drilldown'
                          : '')
                      }
                      // preventDefault stops the input from blurring before
                      // onClick fires — closes the race between mousedown-close
                      // and click-select on iOS Safari.
                      onMouseDown={(event) => event.preventDefault()}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      onClick={() => selectSuggestion(suggestion)}
                    >
                      <span className="address-lookup-v2__option-text">
                        <span className="address-lookup-v2__option-primary">
                          {highlightMatch(lines.primary, trimmedQuery)}
                        </span>
                        {lines.secondary && (
                          <span className="address-lookup-v2__option-secondary">
                            {highlightMatch(lines.secondary, trimmedQuery)}
                          </span>
                        )}
                      </span>
                      {!isTerminal && (
                        <span
                          className="address-lookup-v2__option-affordance"
                          aria-hidden="true"
                        >
                          <span className="address-lookup-v2__option-count">
                            {suggestion.count} addresses
                          </span>
                          <ChevronRightIcon className="address-lookup-v2__option-chevron" />
                        </span>
                      )}
                    </li>
                  );
                })}
            </ul>
          )}
          <span
            id={statusId}
            className="address-lookup-v2__sr-only"
            role="status"
            aria-live="polite"
          >
            {statusMessage}
          </span>
        </div>

        <p className="address-lookup-v2__hint" id={hintId}>
          Pick your address from the list, or fill the fields in below.
        </p>
      </div>

      {/*
        The picked address, still editable — Postcoder gets a flat or a
        building name wrong often enough that the customer has to be able to
        correct it, and a delivery to the wrong door is the expensive kind of
        mistake. `role="group"` rather than a `<fieldset>`: the group needs a
        name for assistive tech, not a second visible heading beside the one
        the field label already carries.
      */}
      <div
        className="address-lookup-v2__details"
        role="group"
        aria-label={`${label} details`}
      >
        <V2AddressInput
          address={address}
          onChange={onAddressFieldChange}
          name="line1"
          placeholder="Address line 1"
          idPrefix={idPrefix}
          autoComplete="address-line1"
          error={fieldErrors?.line1}
          span="full"
        />
        <V2AddressInput
          address={address}
          onChange={onAddressFieldChange}
          name="line2"
          placeholder="Address line 2"
          idPrefix={idPrefix}
          autoComplete="address-line2"
          span="full"
        />
        <V2AddressInput
          address={address}
          onChange={onAddressFieldChange}
          name="city"
          placeholder="Town or city"
          idPrefix={idPrefix}
          autoComplete="address-level2"
          error={fieldErrors?.city}
        />
        <V2AddressInput
          address={address}
          onChange={onAddressFieldChange}
          name="county"
          placeholder="County"
          idPrefix={idPrefix}
          autoComplete="address-level1"
        />
        <V2AddressInput
          address={address}
          onChange={onAddressFieldChange}
          name="postCode"
          placeholder="Postcode"
          idPrefix={idPrefix}
          autoComplete="postal-code"
          autoCapitalize="characters"
          error={fieldErrors?.postCode}
        />
        <V2AddressInput
          address={address}
          onChange={onAddressFieldChange}
          name="country"
          placeholder="Country"
          readOnly={onlyUnitedKingdom}
          defaultValue={onlyUnitedKingdom ? 'United Kingdom' : undefined}
          idPrefix={idPrefix}
          autoComplete="country-name"
        />
      </div>

      {cityValidationError && (
        <div
          className="city-validation-error address-lookup-v2__city-error"
          role="alert"
        >
          {cityValidationError}
        </div>
      )}
      {children}
    </div>
  );
}

interface V2AddressInputProps {
  onChange: (
    key: keyof Address,
  ) => (
    value: ChangeEvent<HTMLInputElement> | FocusEvent<HTMLInputElement>,
  ) => void;
  placeholder: string;
  name: keyof Address;
  address: Address;
  readOnly?: boolean;
  defaultValue?: string;
  idPrefix?: string;
  autoComplete?: string;
  autoCapitalize?: string;
  error?: string | null;
  /** `full` spans both columns — the two street lines want the whole measure. */
  span?: 'full';
}

/**
 * One line of the address.
 *
 * The label used to render *after* the input, under a 1px `div.line` standing
 * in for a border — a floating-label pattern with the float missing. It read
 * as a caption for the field above it, and the field itself had no visible
 * boundary until you clicked into it. Label above, real input chrome below,
 * which is what every other form in the app now does.
 *
 * `autoComplete` is new: the browser knows the customer's address and could
 * not offer it, because these fields never said what they were.
 */
function V2AddressInput({
  onChange,
  placeholder,
  name,
  address,
  readOnly = false,
  defaultValue,
  idPrefix = '',
  autoComplete,
  autoCapitalize,
  error,
  span,
}: V2AddressInputProps) {
  const inputId = idPrefix ? `${idPrefix}-${name}` : (name as string);
  const errorId = `${inputId}-error`;

  return (
    <div
      className={`address-field${span === 'full' ? ' address-field--full' : ''}${
        error ? ' address-field--invalid' : ''
      }`}
    >
      <label className="address-field__label" htmlFor={inputId}>
        {placeholder}
      </label>
      <input
        className="address-field__input"
        id={inputId}
        onChange={onChange(name)}
        onBlur={onChange(name)}
        value={address[name] ?? defaultValue ?? ''}
        readOnly={readOnly}
        autoComplete={autoComplete}
        autoCapitalize={autoCapitalize}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
      />
      {error && (
        <p className="address-field__error" id={errorId} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
